// Client-side calculation engine — mirrors backend compute_analysis
// so the Offer Scenario Lab can recompute instantly without a round-trip.

export const DEFAULT_CONFIG = {
  va_funding_fee: {
    first_use:      { "0-4.99": 2.15, "5-9.99": 1.50, "10+": 1.25 },
    subsequent_use: { "0-4.99": 3.30, "5-9.99": 1.50, "10+": 1.25 },
  },
  defaults: {
    appraisal: 725,
    general_inspection: 450,
    specialty_inspections: {
      sewer_scope: 175, septic: 400, well: 250, water_quality: 150,
      pest: 125, roof: 250, structural_engineer: 550, hvac: 200,
      electrical: 200, plumbing: 200, mold: 400, radon: 175,
    },
    closing_cost_pct: 3.0,
    prepaid_months_insurance: 3,
    prepaid_months_taxes: 2,
  },
  score_thresholds: { excellent: 85, strong: 70, fair: 55 },
};

const n = (v) => (v === "" || v == null ? 0 : Number(v) || 0);

export function vaFundingFeePct(cfg, downPct, firstUse, exempt) {
  if (exempt) return 0;
  const t = cfg.va_funding_fee[firstUse ? "first_use" : "subsequent_use"];
  if (downPct < 5) return t["0-4.99"];
  if (downPct < 10) return t["5-9.99"];
  return t["10+"];
}

export function compute(inputs, cfg = DEFAULT_CONFIG) {
  const isMissing = (k) => {
    const v = inputs[k];
    return v == null || (typeof v === "string" && v.trim() === "");
  };
  const missingInputs = ["interest_rate", "annual_taxes", "annual_insurance"].filter(isMissing);
  const price = n(inputs.purchase_price);
  const dpVal = n(inputs.down_payment);
  const dpIsPct = !!inputs.down_payment_is_pct;
  const downAmount = dpIsPct ? (price * dpVal) / 100 : dpVal;
  const downPct = price ? (downAmount / price) * 100 : 0;

  const rate = n(inputs.interest_rate);
  const termYears = Number(inputs.term_years || 30);
  const financing = inputs.financing_type || "va";

  const baseLoan = Math.max(price - downAmount, 0);

  let fundingFee = 0;
  if (financing === "va") {
    const firstUse = (inputs.va_first_use || "first_use") === "first_use";
    const exempt = inputs.va_exempt === "yes";
    fundingFee = (baseLoan * vaFundingFeePct(cfg, downPct, firstUse, exempt)) / 100;
  }
  const totalFinanced = baseLoan + fundingFee;

  const nMonths = termYears * 12;
  const r = rate / 100 / 12;
  const pi = r > 0 && nMonths > 0
    ? (totalFinanced * (r * Math.pow(1 + r, nMonths))) / (Math.pow(1 + r, nMonths) - 1)
    : nMonths ? totalFinanced / nMonths : 0;

  const taxesMonthly = n(inputs.annual_taxes) / 12;
  const insMonthly = n(inputs.annual_insurance) / 12;
  const hoa = n(inputs.hoa_monthly);
  const otherHousing = n(inputs.other_housing_monthly);
  const monthlyTotal = pi + taxesMonthly + insMonthly + hoa + otherHousing;

  const cc = inputs.closing_costs || {};
  const closingCosts = cc.mode === "detailed"
    ? Object.values(cc.detailed || {}).reduce((s, v) => s + n(v), 0)
    : (cc.is_pct ? (price * n(cc.value)) / 100 : n(cc.value));
  const sellerCredit = n(inputs.seller_credit);
  const lenderCredit = n(inputs.lender_credit);
  const otherCredit = n(inputs.other_credit);
  const prepaids = n(inputs.prepaids);
  const discountPoints = n(inputs.discount_points);
  const otherClosing = n(inputs.other_closing);
  const totalCredits = sellerCredit + lenderCredit + otherCredit;

  const preItems = inputs.pre_closing_items || [];
  let cashBeforeClosing = 0;
  let moneyLikelySpent = 0;
  let moneyTemporarilyTiedUp = 0;
  let earnestCredited = 0;
  for (const it of preItems) {
    const amt = n(it.amount);
    if (it.paid_before_closing) {
      cashBeforeClosing += amt;
      if (it.credited_at_closing) moneyTemporarilyTiedUp += amt;
      else moneyLikelySpent += amt;
    }
    const isEarnest = it.type === "earnest_money" || /earnest/i.test(it.label || "");
    if (isEarnest && it.credited_at_closing) earnestCredited += amt;
  }

  // VA funding fee is financed into the loan; NOT included in cash needed.
  const grossAtClosing = downAmount + closingCosts + prepaids + discountPoints + otherClosing;
  // Credits can only offset eligible amounts due at closing and can never
  // reduce cash-at-closing below $0 or reduce pre-closing cash expenses.
  const eligibleForCredits = Math.max(grossAtClosing - earnestCredited, 0);
  const creditsApplied = Math.min(totalCredits, eligibleForCredits);
  const unusedCredit = Math.max(totalCredits - eligibleForCredits, 0);
  const additionalCashAtClosing = Math.max(grossAtClosing - totalCredits - earnestCredited, 0);
  const maxTransactionCash = cashBeforeClosing + additionalCashAtClosing;

  const income = n(inputs.monthly_income);
  const debts = ["car_payments","credit_card_min","student_loans","personal_loans","child_support","other_debt"]
    .reduce((s, k) => s + n(inputs[k]), 0);
  const dti = income ? ((debts + monthlyTotal) / income) * 100 : null;
  const housingToIncome = income ? (monthlyTotal / income) * 100 : null;

  const cashAvailable = n(inputs.cash_available);
  const desiredReserve = n(inputs.desired_reserve);
  const cashRemaining = cashAvailable ? cashAvailable - maxTransactionCash : null;
  const cashGap = cashAvailable ? Math.max(maxTransactionCash - cashAvailable, 0) : 0;
  const reserveShortfall = (desiredReserve && cashRemaining != null)
    ? Math.max(desiredReserve - cashRemaining, 0) : null;

  // Score only when minimum inputs exist
  const scoreAvailable = !!(price > 0 && rate > 0 && (income || cashAvailable));
  let score = null, scoreLabel = null;
  const reasons = { strengths: [], watch: [] };
  if (scoreAvailable) {
    let s = 100;
    if (dti != null) {
      if (dti > 50) { s -= 25; reasons.watch.push("Estimated DTI is high relative to typical guidelines."); }
      else if (dti > 43) { s -= 15; reasons.watch.push("Estimated DTI is moderately elevated."); }
      else if (dti < 36) reasons.strengths.push("Estimated DTI is within a comfortable range.");
    }
    if (cashAvailable) {
      if (cashGap > 0) { s -= 30; reasons.watch.push("Reported cash may be insufficient for max transaction need."); }
      else if (cashRemaining && cashRemaining > maxTransactionCash * 0.25)
        reasons.strengths.push("Meaningful cash cushion remains after purchase.");
    }
    if (reserveShortfall && reserveShortfall > 0 && desiredReserve > 0) {
      const ratio = reserveShortfall / desiredReserve;
      if (ratio > 0.50) {
        s -= 30;
        reasons.watch.push("Estimated cash cushion falls substantially short of your desired reserve.");
      } else if (ratio > 0.25) {
        s -= 20;
        reasons.watch.push("Estimated cash cushion is noticeably below your desired reserve.");
      } else if (ratio > 0.10) {
        s -= 12;
        reasons.watch.push("Estimated cash cushion is somewhat below your desired reserve.");
      } else {
        s -= 6;
        reasons.watch.push("Estimated cash cushion is slightly below your desired reserve.");
      }
    }
    if (closingCosts > 0 && totalCredits >= closingCosts * 0.75) {
      reasons.strengths.push("Credits offset most closing costs."); s += 3;
    }
    if (income && monthlyTotal / income > 0.40) {
      s -= 10; reasons.watch.push("Housing payment is a large share of reported income.");
    }
    score = Math.max(0, Math.min(100, s));
    if (score >= 99 && reasons.strengths.length < 2) score = 98;
    scoreLabel = score >= 85 ? "Strong Cash Position"
               : score >= 70 ? "Moderate Purchase Pressure"
               : "Higher Purchase Pressure";
  }

  return {
    purchase_price: price, down_payment_amount: downAmount, down_payment_pct: downPct,
    base_loan: baseLoan, funding_fee: fundingFee, funding_fee_financed: true,
    total_financed: totalFinanced,
    principal_interest: pi, taxes_monthly: taxesMonthly, insurance_monthly: insMonthly,
    hoa_monthly: hoa, other_housing_monthly: otherHousing, monthly_total: monthlyTotal,
    closing_costs: closingCosts, total_credits: totalCredits,
    credits_applied: creditsApplied, unused_credit: unusedCredit,
    prepaids, discount_points: discountPoints,
    other_closing: otherClosing, gross_at_closing: grossAtClosing,
    earnest_credited_at_closing: earnestCredited,
    additional_cash_at_closing: additionalCashAtClosing,
    cash_before_closing: cashBeforeClosing,
    money_likely_spent: moneyLikelySpent,
    money_temporarily_tied_up: moneyTemporarilyTiedUp,
    max_transaction_cash: maxTransactionCash,
    monthly_debts: debts, dti, housing_to_income_ratio: housingToIncome, cash_available: cashAvailable,
    desired_reserve: desiredReserve,
    cash_remaining: cashRemaining, cash_gap: cashGap,
    reserve_shortfall: reserveShortfall,
    score_available: scoreAvailable, score, score_label: scoreLabel,
    score_reasons: reasons,
    missing_inputs: missingInputs,
    monthly_incomplete: missingInputs.length > 0,
  };
}

// Dynamic funding-fee tier text for the VA down-payment field.
export function vaFundingTierText(inputs) {
  const price = n(inputs.purchase_price);
  const dpVal = n(inputs.down_payment);
  const dpAmount = inputs.down_payment_is_pct ? (price * dpVal) / 100 : dpVal;
  const dpPct = price ? (dpAmount / price) * 100 : 0;
  const isFirst = (inputs.va_first_use || "first_use") === "first_use";
  const label = isFirst ? "first use" : "subsequent use";
  const t1 = isFirst ? "2.15%" : "3.30%";
  if (!price) return `Enter a purchase price to see the funding-fee tier for ${label}.`;
  if (dpAmount === 0) return `0% down — estimated VA funding-fee tier: ${t1} for ${label}.`;
  const pctStr = dpPct.toFixed(dpPct < 1 ? 2 : 1);
  if (dpPct < 5)   return `$${Math.round(dpAmount).toLocaleString()} = ${pctStr}% down — still within the less-than-5% funding-fee tier (${t1}).`;
  if (dpPct < 10)  return `$${Math.round(dpAmount).toLocaleString()} = ${pctStr}% down — 5–9.99% tier: estimated 1.5%.`;
  return `$${Math.round(dpAmount).toLocaleString()} = ${pctStr}% down — 10%+ tier: estimated 1.25%.`;
}

export function scoreBand(score) {
  if (score == null) return { label: "Not enough information yet", color: "#9AA0A6" };
  if (score >= 85) return { label: "Strong Cash Position", color: "#4A7C59" };
  if (score >= 70) return { label: "Moderate Purchase Pressure", color: "#E2A03F" };
  return { label: "Higher Purchase Pressure", color: "#C95D63" };
}

export const emptyInputs = () => ({
  financing_type: "va",
  purchase_price: "",
  zip: "",
  annual_taxes: "",
  annual_insurance: "",
  hoa_monthly: "",
  other_housing_monthly: "",
  term_years: 30,
  interest_rate: "",
  down_payment: 0,
  down_payment_is_pct: false,
  va_exempt: "no",
  va_first_use: "first_use",
  monthly_income: "",
  car_payments: "", credit_card_min: "", student_loans: "",
  personal_loans: "", child_support: "", other_debt: "",
  cash_available: "",
  desired_reserve: "",
  credit_score_range: "",
  closing_costs: { is_pct: true, value: 3, mode: "simple", detailed: { lender: "", title: "", recording: "", other: "" } },
  has_lender_estimate: null,
  seller_credit: "", lender_credit: "", other_credit: "",
  prepaids: "", discount_points: "", other_closing: "",
  pre_closing_items: [
    { id: "em", type: "earnest_money", label: "Earnest Money Deposit", amount: "", paid_before_closing: true, refundable: "not_sure", credited_at_closing: true, notes: "" },
    { id: "gi", type: "general_inspection", label: "General Home Inspection", amount: "", paid_before_closing: true, refundable: "no", credited_at_closing: false, notes: "" },
    { id: "ap", type: "appraisal", label: "VA Appraisal", amount: "", paid_before_closing: true, refundable: "no", credited_at_closing: false, notes: "" },
  ],
});

// Realistic sample VA home used by the first-visit onboarding nudge.
// Represents a typical first-use, zero-down VA purchase at a modest price
// point so a new visitor immediately sees the value of the full analysis.
export const sampleVAInputs = () => ({
  financing_type: "va",
  purchase_price: 385000,
  zip: "23604",
  annual_taxes: 3465,       // ~0.9% effective rate
  annual_insurance: 1800,
  hoa_monthly: 0,
  other_housing_monthly: 0,
  term_years: 30,
  interest_rate: 6.5,
  down_payment: 0,
  down_payment_is_pct: false,
  va_exempt: "no",
  va_first_use: "first_use",
  monthly_income: 7800,
  car_payments: 425, credit_card_min: 85, student_loans: 0,
  personal_loans: 0, child_support: 0, other_debt: 0,
  cash_available: 15000,
  desired_reserve: 5000,
  credit_score_range: "700-739",
  closing_costs: { is_pct: true, value: 3, mode: "simple", detailed: { lender: "", title: "", recording: "", other: "" } },
  has_lender_estimate: false,
  seller_credit: 6000, lender_credit: 0, other_credit: 0,
  prepaids: "", discount_points: "", other_closing: "",
  pre_closing_items: [
    { id: "em", type: "earnest_money", label: "Earnest Money Deposit", amount: 3000, paid_before_closing: true, refundable: "not_sure", credited_at_closing: true, notes: "" },
    { id: "gi", type: "general_inspection", label: "General Home Inspection", amount: 450, paid_before_closing: true, refundable: "no", credited_at_closing: false, notes: "" },
    { id: "ap", type: "appraisal", label: "VA Appraisal", amount: 900, paid_before_closing: true, refundable: "no", credited_at_closing: false, notes: "" },
  ],
});

